/**
 * The purchase request form.
 *
 * The single screen a teacher will judge the whole system by, so it is built to be
 * finishable: totals compute as you type, the budget position appears before you
 * submit rather than after, and the readiness check lists everything missing at once
 * instead of one problem per attempt.
 *
 * Line arithmetic here is provisional. The server recalculates every total from the
 * inputs — this is only so the person filling the form can see what they are asking
 * for (money.ts).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { addMoney, formatMoney, multiplyMoney, vatOf } from '@asp/shared';
import { api, ApiError, query } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import {
  Badge,
  Banner,
  Button,
  Card,
  Field,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from '../components/ui.tsx';

interface LineDraft {
  key: number;
  description: string;
  quantity: string;
  uom: string;
  unitPrice: string;
  vatRate: string;
}

const emptyLine = (key: number): LineDraft => ({
  key,
  description: '',
  quantity: '1',
  uom: '',
  unitPrice: '',
  vatRate: '20',
});

export function NewRequest() {
  const navigate = useNavigate();
  const { session, activeSchoolId } = useSession();

  const [schoolId, setSchoolId] = useState(
    activeSchoolId ?? session?.accessibleSchools[0]?.id ?? '',
  );
  const [departmentId, setDepartmentId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [isNewSupplier, setIsNewSupplier] = useState(false);
  const [proposedSupplierName, setProposedSupplierName] = useState('');
  const [budgetCodeId, setBudgetCodeId] = useState('');
  const [costCentreId, setCostCentreId] = useState('');
  const [requiredDeliveryDate, setRequiredDeliveryDate] = useState('');
  const [priority, setPriority] = useState('normal');
  const [businessJustification, setBusinessJustification] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(1)]);
  const [nextKey, setNextKey] = useState(2);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Reference data, refetched when the school changes because departments, budget
  // codes and even the VAT default are per school.
  const { data: departments } = useQuery({
    queryKey: ['departments', schoolId],
    queryFn: () =>
      api.get<{ departments: Array<{ id: string; name: string; head_name: string | null }> }>(
        `/api/departments${query({ schoolId })}`,
      ),
    enabled: Boolean(schoolId),
  });

  const { data: suppliers } = useQuery({
    queryKey: ['suppliers', schoolId],
    queryFn: () =>
      api.get<{ suppliers: Array<{ id: string; name: string; usable_here: boolean | null }> }>(
        `/api/suppliers${query({ schoolId })}`,
      ),
    enabled: Boolean(schoolId),
  });

  const { data: codes } = useQuery({
    queryKey: ['budget-codes', schoolId],
    queryFn: () =>
      api.get<{
        budgetCodes: Array<{ id: string; code: string; name: string }>;
        costCentres: Array<{ id: string; code: string; name: string }>;
      }>(`/api/budget-codes${query({ schoolId })}`),
    enabled: Boolean(schoolId),
  });

  const { data: settings } = useQuery({
    queryKey: ['form-settings', schoolId],
    queryFn: () =>
      api.get<{
        settings: {
          vat_rates: number[];
          default_vat_rate: string;
          quote_required_above: string;
          require_delivery_date: boolean;
          escalation_threshold: string;
          quotes_enabled: boolean;
          budget_control_enabled: boolean;
        };
      }>(`/api/form-settings${query({ schoolId })}`),
    enabled: Boolean(schoolId),
  });

  const s = settings?.settings;

  // Provisional totals, for the person typing.
  const totals = useMemo(() => {
    let net = '0.00';
    let vat = '0.00';
    for (const line of lines) {
      if (!line.unitPrice || !line.quantity) continue;
      try {
        const lineNet = multiplyMoney(line.unitPrice, Number(line.quantity));
        net = addMoney(net, lineNet);
        vat = addMoney(vat, vatOf(lineNet, Number(line.vatRate || '0')));
      } catch {
        // A half-typed number is not an error worth showing yet.
      }
    }
    return { net, vat, total: addMoney(net, vat) };
  }, [lines]);

  const overThreshold =
    s?.escalation_threshold && Number(totals.total) > Number(s.escalation_threshold);
  const quoteNeeded =
    s?.quotes_enabled && s?.quote_required_above && Number(totals.total) > Number(s.quote_required_above);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/api/requests', {
        schoolId,
        departmentId,
        supplierId: supplierId || undefined,
        isNewSupplier,
        proposedSupplierName: proposedSupplierName || undefined,
        budgetCodeId: budgetCodeId || undefined,
        costCentreId: costCentreId || undefined,
        requiredDeliveryDate: requiredDeliveryDate || undefined,
        priority,
        businessJustification,
        lines: lines
          .filter((l) => l.description.trim() && l.unitPrice)
          .map((l) => ({
            description: l.description.trim(),
            quantity: Number(l.quantity),
            uom: l.uom || undefined,
            unitPrice: l.unitPrice,
            vatRate: Number(l.vatRate || '0'),
          })),
      }),
    onSuccess: (result) => navigate(`/requests/${result.id}`),
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fields ?? {});
      } else {
        setError('That could not be saved.');
      }
    },
  });

  if (!session) return null;

  const updateLine = (key: number, patch: Partial<LineDraft>) =>
    setLines((current) => current.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  return (
    <>
      <PageHeader
        title="New purchase request"
        description="Saved as a draft first — nothing is sent for approval until you submit it."
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
        className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]"
      >
        <div className="space-y-4">
          <Card title="Where and who">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="School" required>
                <Select
                  required
                  value={schoolId}
                  onChange={(e) => {
                    setSchoolId(e.target.value);
                    // These are all school-scoped; keeping a stale value would let
                    // someone submit against another school's department.
                    setDepartmentId('');
                    setSupplierId('');
                    setBudgetCodeId('');
                    setCostCentreId('');
                  }}
                >
                  <option value="">Choose…</option>
                  {session.accessibleSchools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.name}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field
                label="Department"
                required
                error={fieldErrors.departmentId}
                hint={
                  departments?.departments.find((d) => d.id === departmentId)?.head_name
                    ? `Reviewed first by ${departments.departments.find((d) => d.id === departmentId)!.head_name}`
                    : undefined
                }
              >
                <Select
                  required
                  value={departmentId}
                  onChange={(e) => setDepartmentId(e.target.value)}
                  disabled={!schoolId}
                >
                  <option value="">Choose…</option>
                  {departments?.departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </Card>

          <Card title="Supplier">
            <div className="space-y-4">
              {!isNewSupplier ? (
                <Field label="Supplier" required error={fieldErrors.supplierId}>
                  <Select
                    value={supplierId}
                    onChange={(e) => setSupplierId(e.target.value)}
                    disabled={!schoolId}
                  >
                    <option value="">Choose…</option>
                    {suppliers?.suppliers.map((sup) => (
                      <option key={sup.id} value={sup.id} disabled={sup.usable_here === false}>
                        {sup.name}
                        {sup.usable_here === false ? ' — not available to this school' : ''}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <Field
                  label="New supplier name"
                  required
                  hint="Head Office will check the supplier before an order can be placed. This does not delay approval."
                >
                  <Input
                    value={proposedSupplierName}
                    onChange={(e) => setProposedSupplierName(e.target.value)}
                    placeholder="Company name"
                  />
                </Field>
              )}

              <label className="flex items-center gap-2 text-[13px] text-[var(--ink-secondary)]">
                <input
                  type="checkbox"
                  checked={isNewSupplier}
                  onChange={(e) => {
                    setIsNewSupplier(e.target.checked);
                    if (e.target.checked) setSupplierId('');
                    else setProposedSupplierName('');
                  }}
                  className="size-4 rounded border-[var(--border-default)] accent-navy-700"
                />
                This supplier is not on the list
              </label>
            </div>
          </Card>

          {/* Items. One line by default — most requests are one thing — with "add
              another" for the quotations that cover several. */}
          <Card
            title="What you need"
            actions={
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setLines((current) => [...current, emptyLine(nextKey)]);
                  setNextKey((k) => k + 1);
                }}
              >
                Add another item
              </Button>
            }
            padded={false}
          >
            <div className="space-y-3 p-4">
              {lines.map((line, index) => (
                <div
                  key={line.key}
                  className="grid gap-3 rounded-md border border-[var(--border-subtle)] bg-[var(--surface-2)] p-3 sm:grid-cols-[minmax(0,2fr)_5rem_6rem_5rem_auto]"
                >
                  <Field label={index === 0 ? 'Description' : `Item ${index + 1}`} required>
                    <Input
                      required
                      value={line.description}
                      onChange={(e) => updateLine(line.key, { description: e.target.value })}
                      placeholder="e.g. A3 laminator, 100 micron"
                    />
                  </Field>
                  <Field label="Quantity" required>
                    <Input
                      required
                      inputMode="decimal"
                      value={line.quantity}
                      onChange={(e) => updateLine(line.key, { quantity: e.target.value })}
                    />
                  </Field>
                  <Field label="Unit price" required>
                    <Input
                      required
                      inputMode="decimal"
                      placeholder="0.00"
                      value={line.unitPrice}
                      onChange={(e) => updateLine(line.key, { unitPrice: e.target.value })}
                    />
                  </Field>
                  <Field label="VAT">
                    <Select
                      value={line.vatRate}
                      onChange={(e) => updateLine(line.key, { vatRate: e.target.value })}
                    >
                      {(s?.vat_rates ?? [0, 5, 20]).map((rate) => (
                        <option key={rate} value={String(rate)}>
                          {rate}%
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <div className="flex items-end">
                    {lines.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setLines((c) => c.filter((l) => l.key !== line.key))}
                        aria-label={`Remove item ${index + 1}`}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[var(--border-subtle)] px-4 py-3">
              <Table className="min-w-0">
                <tbody>
                  <Tr>
                    <Td className="border-0 py-1 text-[13px] text-[var(--ink-secondary)]">Net</Td>
                    <Td numeric className="border-0 py-1">
                      {formatMoney(totals.net)}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td className="border-0 py-1 text-[13px] text-[var(--ink-secondary)]">VAT</Td>
                    <Td numeric className="border-0 py-1">
                      {formatMoney(totals.vat)}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td className="border-0 py-1 text-[13px] font-semibold">Total</Td>
                    <Td numeric className="border-0 py-1 text-base font-semibold">
                      {formatMoney(totals.total)}
                    </Td>
                  </Tr>
                </tbody>
              </Table>
            </div>
          </Card>

          <Card title="Why you need it">
            <Field
              label="Business justification"
              required
              hint="A sentence or two. Approvers read this first, and a good one gets approved faster."
              error={fieldErrors.businessJustification}
            >
              <Textarea
                required
                rows={4}
                value={businessJustification}
                onChange={(e) => setBusinessJustification(e.target.value)}
                placeholder="What this is for, and what happens if it is not bought."
              />
            </Field>
          </Card>

          <Card title="Delivery and coding">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Needed by"
                required={s?.require_delivery_date ?? true}
                error={fieldErrors.requiredDeliveryDate}
              >
                <Input
                  type="date"
                  required={s?.require_delivery_date ?? true}
                  min={new Date().toISOString().slice(0, 10)}
                  value={requiredDeliveryDate}
                  onChange={(e) => setRequiredDeliveryDate(e.target.value)}
                />
              </Field>

              <Field label="Priority">
                <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </Select>
              </Field>

              <Field label="Budget code" hint="Ask your department head if you are not sure.">
                <Select
                  value={budgetCodeId}
                  onChange={(e) => setBudgetCodeId(e.target.value)}
                  disabled={!schoolId}
                >
                  <option value="">Not specified</option>
                  {codes?.budgetCodes.map((code) => (
                    <option key={code.id} value={code.id}>
                      {code.code} — {code.name}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Cost centre">
                <Select
                  value={costCentreId}
                  onChange={(e) => setCostCentreId(e.target.value)}
                  disabled={!schoolId}
                >
                  <option value="">Not specified</option>
                  {codes?.costCentres.map((cc) => (
                    <option key={cc.id} value={cc.id}>
                      {cc.code} — {cc.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </Card>
        </div>

        {/* ------------------------------------------------------------ */}
        {/* Side column: what happens next */}
        {/* ------------------------------------------------------------ */}
        <div className="space-y-4">
          <Card title="Summary" className="lg:sticky lg:top-16">
            <p className="text-[12px] font-medium uppercase tracking-wide text-[var(--ink-muted)]">
              Total including VAT
            </p>
            <p className="mt-0.5 text-2xl font-semibold tabular leading-none text-[var(--ink-primary)]">
              {formatMoney(totals.total)}
            </p>

            {!settings ? (
              <div className="mt-4">
                <LoadingState label="Checking the rules for this school" />
              </div>
            ) : (
              <div className="mt-4 space-y-2 text-[13px]">
                <p className="font-medium text-[var(--ink-primary)]">What happens next</p>
                <ol className="space-y-1.5 text-[var(--ink-secondary)]">
                  <li>1. Your head of department or head teacher reviews it.</li>
                  {overThreshold ? (
                    <li className="font-medium text-[var(--ink-primary)]">
                      2. Because it is over {formatMoney(s!.escalation_threshold)}, a Director or Head
                      Office Finance must also approve.
                    </li>
                  ) : (
                    <li>2. Under {formatMoney(s!.escalation_threshold)}, so no senior approval needed.</li>
                  )}
                  <li>{overThreshold ? '3' : '3'}. Finance raises the order with the supplier.</li>
                </ol>

                {quoteNeeded && (
                  <div className="pt-2">
                    <Banner tone="warning" title="A quotation is required">
                      Over {formatMoney(s!.quote_required_above)} you must attach a supplier quotation.
                      You can do that on the next screen, before submitting.
                    </Banner>
                  </div>
                )}

                {overThreshold && (
                  <div className="pt-1">
                    <Badge tone="info">Senior approval required</Badge>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="mt-4">
                <Banner tone="danger">{error}</Banner>
              </div>
            )}

            <div className="mt-4 space-y-2">
              <Button type="submit" variant="primary" size="lg" full loading={create.isPending}>
                Save draft
              </Button>
              <Button type="button" variant="ghost" full onClick={() => navigate('/requests')}>
                Discard
              </Button>
            </div>

            <p className="mt-3 text-[12px] text-[var(--ink-muted)]">
              Saving creates a draft. You attach quotations and submit on the next screen.
            </p>
          </Card>
        </div>
      </form>
    </>
  );
}
